import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Book, MagnifyingGlass, CaretDown, CaretRight, Spinner,
  Download, DownloadSimple, Question, ShieldCheck, House, UsersThree, User, Globe, Briefcase,
  PaperPlaneTilt, X, ChatCircleDots,
} from "@phosphor-icons/react";
import WorkflowDiagram from "@/components/WorkflowDiagram";
import { ManualMarkdown, ManualSectionEditor, ManualEditButton } from "@/pages/ManualSectionEditor";
import api from "@/lib/api";
import { recordHrLibraryView } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import LanguagePicker from "@/components/LanguagePicker";
import { toast } from "sonner";

/**
 * HelpPage — role-aware user manual at /help and /help/:role.
 *
 * Features:
 *  • Sidebar with 5 roles (super_admin / admin / dealer / customer / guest).
 *    Auto-selects the role best matching the logged-in user.
 *  • Each section renders its quick-start summary inline.
 *  • Search filters sections by title/summary substring.
 *  • Download-as-PDF button per role.
 *  • Share-via-email modal lets signed-in users send the manual to anyone.
 *  • UI strings + section content respect the active language (en/fr/es).
 */

const ROLE_META = [
  { id: "super_admin", iconKey: "super_admin", testid: "help-role-super-admin" },
  { id: "admin",       iconKey: "admin",       testid: "help-role-admin" },
  { id: "sales",       iconKey: "sales",       testid: "help-role-sales" },
  { id: "dealer",      iconKey: "dealer",      testid: "help-role-dealer" },
  { id: "customer",    iconKey: "customer",    testid: "help-role-customer" },
  { id: "guest",       iconKey: "guest",       testid: "help-role-guest" },
];

const ROLE_ICONS = {
  super_admin: ShieldCheck,
  admin:       House,
  sales:       Briefcase,
  dealer:      UsersThree,
  customer:    User,
  guest:       Globe,
};

/**
 * iter136.21 — strip Markdown syntax for the collapsed preview line.
 * The full markdown is rendered when the section is expanded; the
 * preview is just the plain-text gist (image alt-text becomes the
 * visible token, ``![alt](url)`` collapses to its alt text).
 */
function stripMarkdownForPreview(s) {
  if (!s) return "";
  return String(s)
    // Images: ![alt](url) → alt
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    // Links: [text](url) → text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // Code fences and inline code → drop the marks, keep content
    .replace(/`{1,3}([^`]+)`{1,3}/g, "$1")
    // Headings, emphasis, list markers
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}



function detectDefaultRole(user) {
  if (!user) return "guest";
  if (user.is_super_admin) return "super_admin";
  if (user.is_admin) return "admin";
  if (user.role === "sales" || user.position?.toLowerCase?.().includes("sales")) return "sales";
  if (user.role === "dealer") return "dealer";
  if (user.email) return "customer";
  return "guest";
}
/**
 * iter136.17 — section-level expanded body. Holds the ref to its
 * own ``WorkflowDiagram`` so the action bar (Download PNG / Print as
 * poster / Email this section) is a single right-aligned row rather
 * than one button buried inside the diagram + a separate row below
 * in a different corner.
 *
 * iter136.20 — summary is rendered as Markdown via ``ManualMarkdown``
 * so super-admin-uploaded screenshots embedded with ``![](url)``
 * appear inline between paragraphs. Super admins also see an "Edit
 * section" button that opens an inline Markdown editor with a
 * formatting toolbar + image upload.
 */
function SectionBody({ sec, role, lang, isAdmin, isSuperAdmin, t, onShareClick, onSectionChanged }) {
  const diagramRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const hasDiagram = Array.isArray(sec.diagram) && sec.diagram.length > 0;
  const handleDownloadPng = (e) => {
    e.stopPropagation();
    diagramRef.current?.downloadPng?.();
  };
  return (
    <div className="px-4 pt-1 pb-4 border-t border-slate-100">
      <div className="pl-8" data-testid={`help-section-summary-${sec.id}`}>
        {/* iter148b — HR-onboarding sections carry a full Markdown `body`
            from the HR library; render that instead of the truncated
            `summary` so employees see the entire document inline. */}
        <ManualMarkdown
          source={sec.category === "hr_onboarding" && sec.body ? sec.body : sec.summary}
          testId={`help-section-summary-md-${sec.id}`}
        />
      </div>
      {hasDiagram && (
        <div className="pl-8" data-testid={`help-section-diagram-${sec.id}`}>
          <WorkflowDiagram
            ref={diagramRef}
            steps={sec.diagram}
            testIdPrefix={`help-step-${sec.id}`}
            sectionTitle={sec.title}
          />
        </div>
      )}
      {isSuperAdmin && editing && (
        <div className="pl-8 mt-3" onClick={(e) => e.stopPropagation()}>
          <ManualSectionEditor
            role={role}
            sectionId={sec.id}
            lang={lang}
            initialSummary={sec.summary}
            isOverridden={Boolean(sec.summary_overridden)}
            onSaved={(newSummary) => { setEditing(false); onSectionChanged?.(sec.id, newSummary); }}
            onCancel={() => setEditing(false)}
            t={t}
          />
        </div>
      )}
      <div className="pl-8 mt-3 flex flex-wrap items-center justify-end gap-2" data-testid={`help-section-actions-${sec.id}`}>
        {isSuperAdmin && !editing && (
          <ManualEditButton
            onClick={() => setEditing(true)}
            isOverridden={Boolean(sec.summary_overridden)}
            t={t}
          />
        )}
        {isAdmin && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onShareClick(); }}
            data-testid={`help-section-share-${sec.id}`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <PaperPlaneTilt size={11} weight="fill" />
            {t("help.preview_email_section", "Email this section to…")}
          </button>
        )}
        {hasDiagram && (
          <button
            type="button"
            onClick={handleDownloadPng}
            data-testid={`help-step-${sec.id}-download`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
            title={t("diagram.download_hint", "Save the flowchart as a PNG image")}
          >
            <DownloadSimple size={11} weight="fill" />
            {t("diagram.download", "Download PNG")}
          </button>
        )}
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/${role}/${sec.id}/poster.pdf?lang=${lang}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`help-section-poster-${sec.id}`}
          className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50"
          title={t("help.download_poster_hint", "Print A4 poster: flowchart + summary")}
        >
          <DownloadSimple size={11} weight="fill" />
          {t("help.download_poster", "Print as poster")}
        </a>
      </div>
    </div>
  );
}




export default function HelpPage({ user }) {
  const { role: roleFromUrl } = useParams();
  const navigate = useNavigate();
  const { lang, setLang, t, languages } = useI18n();
  const initialRole = roleFromUrl || detectDefaultRole(user);

  const [role, setRole] = useState(initialRole);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState({}); // { sectionId: true }

  // Track the user's "real" role separately from the displayed role so admins
  // who navigate to another role's manual see a clear Preview banner.
  const actualRole = useMemo(() => detectDefaultRole(user), [user]);
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const inPreviewMode = isAdmin && role !== actualRole;

  // Sync URL ↔ role state
  useEffect(() => {
    if (roleFromUrl && roleFromUrl !== role) setRole(roleFromUrl);
  }, [roleFromUrl, role]);

  const switchRole = useCallback((newRole) => {
    if (newRole !== role) {
      setRole(newRole);
      setOpenSections({});
      navigate(`/help/${newRole}`);
    }
  }, [navigate, role]);

  // Load manual sections whenever role or lang changes.
  // iter136.20 — exposed as ``reloadManual`` so the inline section
  // editor can refetch after a save without reloading the page.
  const reloadManual = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/manual/${role}`, { params: { lang } });
      setSections(r.data?.sections || []);
    } catch {
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [role, lang]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/manual/${role}`, { params: { lang } });
        if (!cancelled) {
          setSections(r.data?.sections || []);
        }
      } catch {
        if (!cancelled) setSections([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [role, lang]);

  // Honor `#topic={id}&lang={code}` hash from QR-code scans on printed
  // posters: switch language, expand the matching topic, scroll into view.
  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash || sections.length === 0) return;
    const params = new URLSearchParams(hash.replace(/&amp;/g, "&"));
    const topicId = params.get("topic");
    const wantLang = params.get("lang");
    if (wantLang && ["en", "fr", "es"].includes(wantLang) && wantLang !== lang) {
      setLang(wantLang);
      return;   // wait for re-render with new lang
    }
    if (!topicId) return;
    const sec = sections.find((s) => s.id === topicId);
    if (!sec) return;
    setOpenSections((prev) => ({ ...prev, [sec.id]: true }));
    // Defer scroll so the expanded panel is laid out
    setTimeout(() => {
      const el = document.querySelector(`[data-help-topic-anchor="${sec.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [sections, lang]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Filter by search
  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) =>
      (s.title || "").toLowerCase().includes(q) ||
      (s.summary || "").toLowerCase().includes(q)
    );
  }, [sections, search]);

  const toggleSection = useCallback((sid) => {
    setOpenSections((prev) => {
      const next = { ...prev, [sid]: !prev[sid] };
      // iter148 — when an HR-published section is opened, log a view so
      // the assignee's `library_read` checklist item auto-completes.
      // Best-effort: silent on failure.
      if (!prev[sid] && sid.startsWith("hr_")) {
        const sec = sections.find((s) => s.id === sid);
        if (sec?.library_id) recordHrLibraryView(sec.library_id).catch(() => {});
      }
      return next;
    });
  }, [sections]);

  const downloadPdf = useCallback(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/manual/${role}/pdf?lang=${encodeURIComponent(lang)}`;
    window.open(url, "_blank");
  }, [role, lang]);

  // ─── Share manual via email ─────────────────────────────────────
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [sharing, setSharing] = useState(false);
  // When set, the share modal is opened in "section-only" mode and the message
  // is pre-filled with a deep-link to the specific section.
  const [sectionShareTarget, setSectionShareTarget] = useState(null);

  const submitShare = useCallback(async (e) => {
    e?.preventDefault?.();
    const to = shareEmail.trim();
    if (!to || !to.includes("@")) {
      toast.error(t("share.invalid_email", "Please enter a valid email address"));
      return;
    }
    setSharing(true);
    try {
      const senderName = (user?.name || user?.email || "").split("@")[0];
      // When sharing a single section, prefix the message with a quoted summary
      // + a deep-link so the recipient lands directly on that section.
      let outgoingMessage = shareMessage.trim();
      if (sectionShareTarget) {
        const deepLink = `${window.location.origin}/help/${role}#${sectionShareTarget.id}`;
        const sectionLine = `📘 ${sectionShareTarget.title}\n\n${sectionShareTarget.summary}\n\n→ ${deepLink}`;
        outgoingMessage = outgoingMessage ? `${outgoingMessage}\n\n${sectionLine}` : sectionLine;
      }
      await api.post("/manual/share", {
        email: to,
        role,
        lang,
        message: outgoingMessage,
        sender_name: senderName,
      });
      toast.success(
        sectionShareTarget
          ? t("help.preview_email_section_sent", "Section sent").replace("{email}", to)
          : t("share.sent_success", undefined, { email: to })
      );
      setShareOpen(false);
      setShareEmail("");
      setShareMessage("");
      setSectionShareTarget(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Send failed";
      toast.error(detail);
    } finally {
      setSharing(false);
    }
  }, [shareEmail, shareMessage, role, lang, user, t, sectionShareTarget]);

  // ─── Feedback (with AI routing) ─────────────────────────────────
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState(user?.email && !user.email.endsWith("@local.guest") ? user.email : "");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSection, setFeedbackSection] = useState(null);

  const submitFeedback = useCallback(async (e) => {
    e?.preventDefault?.();
    const msg = feedbackMessage.trim();
    if (msg.length < 2) {
      toast.error(t("feedback.required", "Please describe your feedback."));
      return;
    }
    setFeedbackSending(true);
    try {
      const res = await api.post("/manual/feedback", {
        role,
        section_id: feedbackSection?.id || "",
        message: msg,
        contact_email: feedbackEmail.trim(),
        lang,
      });
      const count = (res.data?.recipients || []).length;
      toast.success(
        t("feedback.sent", "Thanks!").replace("{count}", count),
        { description: `${res.data?.category || "general"} · ${res.data?.rationale || ""}` }
      );
      setFeedbackOpen(false);
      setFeedbackMessage("");
      setFeedbackSection(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Send failed";
      toast.error(detail);
    } finally {
      setFeedbackSending(false);
    }
  }, [feedbackMessage, feedbackEmail, feedbackSection, role, lang, t]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-page-root">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-blue-700">
            <Book size={18} weight="bold" />
            <span className="font-bold text-sm">{t("help.title", "Help & Manual")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguagePicker />
            <button
              type="button"
              onClick={downloadPdf}
              data-testid="help-download-pdf"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700"
            >
              <Download size={13} weight="bold" />
              {t("help.download_pdf", "Download as PDF")}
            </button>
            {user?.email && !user.email.endsWith("@local.guest") && (
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                data-testid="help-share-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-blue-200 bg-blue-50 hover:bg-blue-100 text-xs font-semibold text-blue-700"
              >
                <PaperPlaneTilt size={13} weight="bold" />
                {t("share.button", "Share")}
              </button>
            )}
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              data-testid="help-feedback-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-violet-200 bg-violet-50 hover:bg-violet-100 text-xs font-semibold text-violet-700"
            >
              <ChatCircleDots size={13} weight="bold" />
              {t("feedback.button", "Send feedback")}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-12 gap-6">
        {/* Sidebar — role picker */}
        <aside className="col-span-12 md:col-span-3" data-testid="help-sidebar">
          <div className="rounded-md bg-white border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t("help.role_select", "Choose your role")}
            </div>
            <ul>
              {ROLE_META.map((r) => {
                const Icon = ROLE_ICONS[r.iconKey] || Question;
                const active = r.id === role;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => switchRole(r.id)}
                      data-testid={r.testid}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-2 text-sm border-l-4 ${active ? "border-blue-600 bg-blue-50 text-blue-800 font-bold" : "border-transparent hover:bg-slate-50 text-slate-700"}`}
                    >
                      <Icon size={16} weight={active ? "fill" : "regular"} />
                      <span>{t(`help.role_${r.id}`, r.id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Main — sections for the active role */}
        <main className="col-span-12 md:col-span-9" data-testid="help-main">
          {/* Admin preview banner */}
          {inPreviewMode && (
            <div className="mb-3 rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-xs"
                 data-testid="help-admin-preview-banner">
              <ShieldCheck size={14} weight="fill" className="text-amber-600 shrink-0" />
              <span className="text-amber-900 font-semibold flex-1 min-w-0 truncate">
                {t("help.preview_banner", "Admin preview · viewing the {role} manual")
                  .replace("{role}", t(`help.role_${role}`, role))}
              </span>
              <button onClick={() => switchRole(actualRole)}
                data-testid="help-preview-back-btn"
                className="px-2 h-6 rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                {t("help.preview_back", "Back to your role")} ({t(`help.role_${actualRole}`, actualRole)})
              </button>
            </div>
          )}

          {/* Search */}
          <div className="mb-4 relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-2.5 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("help.search_placeholder", "Search the manual…")}
              data-testid="help-search"
              className="w-full h-9 pl-8 pr-3 text-sm rounded-sm border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {loading ? (
            <div className="text-sm text-slate-500" data-testid="help-loading">{t("common.loading", "Loading…")}</div>
          ) : filteredSections.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="help-empty">{t("help.no_search_match", "No sections match your search.")}</div>
          ) : (
            <ol className="space-y-3">
              {filteredSections.map((sec, idx) => {
                const isOpen = openSections[sec.id];
                // iter148 — render a divider just before the first HR
                // section so the "HR & Onboarding" group is visually
                // separated from static role manuals.
                const prev = idx === 0 ? null : filteredSections[idx - 1];
                const isFirstHr = sec.category === "hr_onboarding"
                  && (!prev || prev.category !== "hr_onboarding");
                return (
                  <div key={sec.id} className="space-y-3">
                    {isFirstHr && (
                      <div className="flex items-center gap-2 pt-3 border-t-2 border-indigo-200"
                           data-testid="help-hr-onboarding-divider">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-sm">
                          HR & Onboarding
                        </span>
                        <span className="text-[11px] text-slate-500">
                          Materials authored by HR Admin — onboarding, training, manuals, wizards, tutorials.
                        </span>
                      </div>
                    )}
                    <li className={`rounded-md border bg-white overflow-hidden ${
                          sec.category === "hr_onboarding" ? "border-indigo-200" : "border-slate-200"
                        }`}
                        data-testid={`help-section-${sec.id}`}
                        data-help-topic-anchor={sec.id}>
                    <button
                      type="button"
                      onClick={() => toggleSection(sec.id)}
                      className="w-full flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 text-left"
                      data-testid={`help-section-toggle-${sec.id}`}
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-0.5 w-5 shrink-0">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-slate-800">{sec.title}</h3>
                          {!isOpen && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{stripMarkdownForPreview(sec.summary)}</p>}
                        </div>
                      </div>
                      {isOpen ? <CaretDown size={14} weight="bold" className="text-slate-400 mt-1" /> : <CaretRight size={14} weight="bold" className="text-slate-400 mt-1" />}
                    </button>

                    {isOpen && (
                      <SectionBody
                        sec={sec}
                        role={role}
                        lang={lang}
                        isAdmin={isAdmin}
                        isSuperAdmin={Boolean(user?.is_super_admin)}
                        t={t}
                        onShareClick={() => { setSectionShareTarget(sec); setShareOpen(true); }}
                        onSectionChanged={() => { reloadManual?.(); }}
                      />
                    )}
                  </li>
                  </div>
                );
              })}
            </ol>
          )}
        </main>
      </div>

      {/* Share manual modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" data-testid="help-share-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <PaperPlaneTilt size={16} weight="bold" />
                {sectionShareTarget
                  ? t("help.preview_email_section", "Email this section to…")
                  : t("share.title", "Share manual")}
              </h2>
              <button
                type="button"
                onClick={() => { setShareOpen(false); setSectionShareTarget(null); }}
                data-testid="help-share-close"
                className="p-1 hover:bg-slate-100 rounded-sm"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              {sectionShareTarget
                ? <>📘 <span className="font-semibold text-slate-700">{sectionShareTarget.title}</span></>
                : t("share.subtitle", "Send this manual as a PDF + magic-link to anyone — works without an account.")}
              <br />
              <span className="font-semibold text-slate-700">
                {role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              {" · "}
              <span className="uppercase tracking-wider">{lang}</span>
            </p>
            <form onSubmit={submitShare} className="space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  {t("share.recipient_email", "Recipient email")}
                </label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="help-share-email"
                  className="w-full h-9 px-3 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  {t("share.note_optional", "Personal note (optional)")}
                </label>
                <textarea
                  rows={3}
                  value={shareMessage}
                  onChange={(e) => setShareMessage(e.target.value)}
                  placeholder={t("share.note_placeholder", "Hi! Here's the manual we discussed…")}
                  data-testid="help-share-message"
                  className="w-full px-3 py-2 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShareOpen(false); setSectionShareTarget(null); }}
                  disabled={sharing}
                  className="h-9 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-sm"
                >
                  {t("action.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={sharing || !shareEmail.trim()}
                  data-testid="help-share-submit"
                  className="h-9 px-4 inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white text-sm font-bold rounded-sm"
                >
                  {sharing ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
                  {sharing
                    ? t("common.saving", "Sending…")
                    : (sectionShareTarget
                        ? t("help.preview_email_section_send", "Send section")
                        : t("share.send", "Send"))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Feedback modal */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" data-testid="feedback-modal">
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <ChatCircleDots size={16} weight="bold" />
                {t("feedback.title", "Send feedback")}
              </h2>
              <button type="button"
                onClick={() => { setFeedbackOpen(false); setFeedbackSection(null); }}
                data-testid="feedback-close"
                className="p-1 hover:bg-slate-100 rounded-sm">
                <X size={16} weight="bold" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              {t("feedback.subtitle", "AI routes your message to the right admin.")}
              <br />
              <span className="font-semibold text-slate-700">
                {role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              </span>
              {feedbackSection && <> · 📘 <span className="font-semibold">{feedbackSection.title}</span></>}
              {" · "}
              <span className="uppercase tracking-wider">{lang}</span>
            </p>
            <form onSubmit={submitFeedback} className="space-y-3">
              <div>
                <textarea
                  required autoFocus minLength={2} rows={5}
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder={t("feedback.placeholder", "What's confusing, broken or missing?")}
                  data-testid="feedback-message"
                  className="w-full px-3 py-2 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  {t("feedback.contact_email", "Your email (optional)")}
                </label>
                <input type="email" value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="feedback-email"
                  className="w-full h-9 px-3 text-sm rounded-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button type="button"
                  onClick={() => { setFeedbackOpen(false); setFeedbackSection(null); }}
                  disabled={feedbackSending}
                  className="h-9 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-sm">
                  {t("feedback.cancel", "Cancel")}
                </button>
                <button type="submit"
                  disabled={feedbackSending || feedbackMessage.trim().length < 2}
                  data-testid="feedback-submit"
                  className="h-9 px-4 inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-bold rounded-sm">
                  {feedbackSending ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="bold" />}
                  {feedbackSending ? t("feedback.sending", "Sending…") : t("feedback.send", "Send")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

